
import React, { useState } from 'react';
import Header from '../../components/feature/Header';

const ConsultationPage: React.FC = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    name: '',
    phone: '',
    email: '',
    consultationType: '',
    preferredDate: '',
    preferredTime: '',
    message: '',
    agreeToPrivacy: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.companyName || !formData.name || !formData.phone || !formData.email || !formData.consultationType || !formData.preferredDate || !formData.preferredTime) {
      alert('필수 항목을 모두 입력해주세요.');
      return;
    }

    if (!formData.agreeToPrivacy) {
      alert('개인정보 수집 및 이용에 동의해주세요.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value.toString());
      });

      const response = await fetch('https://readdy.ai/api/form/d3vh6gbjbvf464v00tlg', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          companyName: '',
          name: '',
          phone: '',
          email: '',
          consultationType: '',
          preferredDate: '',
          preferredTime: '',
          message: '',
          agreeToPrivacy: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-blue-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            상담 예약하기
          </h1>
          <p className="text-xl md:text-2xl mb-8">
            올딩스 로지스와 함께 최적의 물류 솔루션을 찾아보세요!
          </p>
          <p className="text-lg opacity-90">
            전문 컨설턴트가 귀하의 비즈니스에 맞는 맞춤형 상담을 제공합니다.
          </p>
        </div>
      </div>

      {/* Consultation Form Section */}
      <div className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">상담 예약하기</h2>
              <p className="text-gray-600">
                아래 정보를 입력해주시면 담당자가 연락드려 상담 일정을 조율해드립니다.
              </p>
            </div>

            <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                    업체명 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="회사명을 입력해주세요"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    담당자명 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="담당자명을 입력해주세요"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    연락처 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="010-0000-0000"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    이메일 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="example@company.com"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="consultationType" className="block text-sm font-medium text-gray-700 mb-2">
                    상담 유형 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      id="consultationType"
                      name="consultationType"
                      value={formData.consultationType}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm appearance-none"
                      required
                    >
                      <option value="">상담 유형을 선택해주세요</option>
                      <option value="fulfillment">풀필먼트 서비스</option>
                      <option value="wms">WMS 시스템</option>
                      <option value="logistics">종합 물류 솔루션</option>
                      <option value="partnership">파트너십 문의</option>
                      <option value="other">기타</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                  </div>
                </div>

                <div>
                  <label htmlFor="preferredDate" className="block text-sm font-medium text-gray-700 mb-2">
                    희망 상담 날짜 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    id="preferredDate"
                    name="preferredDate"
                    value={formData.preferredDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="preferredTime" className="block text-sm font-medium text-gray-700 mb-2">
                    희망 상담 시간 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      id="preferredTime"
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm appearance-none"
                      required
                    >
                      <option value="">시간을 선택해주세요</option>
                      <option value="09:00">09:00</option>
                      <option value="10:00">10:00</option>
                      <option value="11:00">11:00</option>
                      <option value="14:00">14:00</option>
                      <option value="15:00">15:00</option>
                      <option value="16:00">16:00</option>
                      <option value="17:00">17:00</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  상담 내용 (선택사항)
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                  placeholder="상담받고 싶은 내용이나 궁금한 점을 자세히 적어주세요 (최대 500자)"
                />
                <div className="text-right text-sm text-gray-500 mt-1">
                  {formData.message.length}/500
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="agreeToPrivacy"
                  name="agreeToPrivacy"
                  checked={formData.agreeToPrivacy}
                  onChange={handleInputChange}
                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  required
                />
                <label htmlFor="agreeToPrivacy" className="text-sm text-gray-700">
                  개인정보 수집 및 이용에 동의합니다. <span className="text-red-500">*</span>
                  <button
                    type="button"
                    onClick={() => setShowPrivacyModal(true)}
                    className="text-blue-600 hover:text-blue-800 underline ml-1"
                  >
                    전문보기
                  </button>
                </label>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <i className="ri-check-circle-line text-green-500 text-xl mr-3"></i>
                    <p className="text-green-700">
                      상담 예약이 성공적으로 접수되었습니다. 담당자가 곧 연락드리겠습니다.
                    </p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <i className="ri-error-warning-line text-red-500 text-xl mr-3"></i>
                    <p className="text-red-700">
                      예약 접수 중 오류가 발생했습니다. 다시 시도해주세요.
                    </p>
                  </div>
                </div>
              )}

              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? '접수 중...' : '상담 예약하기'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Contact Info Section */}
      <div className="bg-gray-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">상담 문의</h2>
            <p className="text-gray-600">
              급하신 경우 아래 연락처로 직접 문의해주세요.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">전화 상담</h3>
              <p className="text-gray-600 mb-2">평일 09:00 - 18:00</p>
              <p className="text-blue-600 font-medium">010-3099-5677</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">이메일 상담</h3>
              <p className="text-gray-600 mb-2">24시간 접수 가능</p>
              <p className="text-blue-600 font-medium">ydhan66@gmail.com</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-time-line text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">상담 시간</h3>
              <p className="text-gray-600 mb-2">평일 09:00 - 18:00</p>
              <p className="text-gray-600">토/일/공휴일 휴무</p>
            </div>
          </div>
        </div>
      </div>

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900">개인정보 수집 및 이용 동의</h3>
                <button
                  onClick={() => setShowPrivacyModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              <div className="text-sm text-gray-700 space-y-4">
                <p>
                  '올딩스 로지스' 는 (이하 '올딩스 로지스'는) 고객님의 개인정보를 중요시하며, "정보통신망 이용촉진 및 정보보호"에 관한 법률을 준수하고 있습니다.
                </p>
                <p>
                  회사는 개인정보취급방침을 통하여 고객님께서 제공하시는 개인정보가 어떠한 용도와 방식으로 이용되고 있으며, 개인정보보호를 위해 어떠한 조치가 취해지고 있는지 알려드립니다.
                </p>
                <p>
                  올딩스 로지스는 개인정보취급방침을 개정하는 경우 웹사이트 공지사항(또는 개별공지)을 통하여 공지할 것입니다.
                </p>
                <p>ο 본 방침은 : 2025 년 11 월 01 일 부터 시행됩니다.</p>
                
                <h4 className="font-semibold text-gray-900">■ 수집하는 개인정보 항목</h4>
                <p>올딩스 로지스는 상담, 견적요청 등등을 위해 아래와 같은 개인정보를 수집하고 있습니다.</p>
                <p>ο 수집항목 : 업체명, 담당자명, 연락처, 이메일, 상담내용</p>
                <p>ο 개인정보 수집방법 : 홈페이지(상담예약하기) 를 통한 예약</p>
                
                <h4 className="font-semibold text-gray-900">■ 개인정보의 수집 및 이용목적</h4>
                <p>올딩스 로지스는 수집한 개인정보를 다음의 목적을 위해 활용합니다.</p>
                <p>ο 상담 예약 확인 및 일정 조율을 위한 연락</p>
                
                <h4 className="font-semibold text-gray-900">■ 개인정보의 수집 및 보유기간</h4>
                <p>고객의 개인정보는 수집 및 이용목적이 달성된 후에는 해당 정보를 지체 없이 파기하며, 보유기간은 최대 3년을 넘기지 않는 것을 원칙으로 합니다.</p>
                
                <h4 className="font-semibold text-gray-900">■ 개인정보에 관한 민원서비스</h4>
                <p>올딩스 로지스는 고객의 개인정보를 보호하고 개인정보와 관련한 불만을 처리하기 위하여 아래와 같이 개인정보관리책임자를 지정하고 있습니다.</p>
                <p>개인정보관리책임자 : 한영대</p>
                <p>전화번호 : 010-3099-5677</p>
                <p>이메일 : ydhan66@gmail.com</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultationPage;
